import React, { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { CVT_STATUS_MAP, CvtStatus } from '@packages/models/api/cvt';
import { StyleSheet } from 'react-native';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Typography, Icon, Button } from '@packages/ui/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@web/core/hooks';
import colors from '@packages/core/styles/colors';
import ReactDOM from 'react-dom';
import { InputPickerHelper } from './input-picker-helper';

const getFilterOptions = () => ({
    status: [
        { label: CVT_STATUS_MAP[CvtStatus.SENT_CVT_TO_SBR], value: 'Pending' },
        { label: CVT_STATUS_MAP[CvtStatus.RECEIVED_RESPONSE_FROM_SBR], value: 'Completed' },
        { label: CVT_STATUS_MAP[CvtStatus.ERROR_PROCESSING_DIAGNOSTIC_DATA], value: 'Failed' },
    ],
});

const useStyles = createUseStyles({
    panel: {
        backgroundColor: colors.white,
        borderRadius: 8,
        bottom: 0,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 5,
    },
    panelHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayTwo,
        borderBottom: `1px solid ${colors.grayThree}`,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        display: 'flex',
        height: 56,
        justifyContent: 'space-between',
        padding: '0px 24px',
    },
    panelScrollContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflowY: 'auto',
        padding: 24,
    },
    dateContainer: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        justifyContent: 'flex-start',
        marginBottom: 16,
        width: '100%',
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 24,
    },
    closeButton: {
        borderRadius: 4,
        padding: 8,
        transition: 'background 0.2s ease-in-out',
        '&:hover': { backgroundColor: colors.grayThree, cursor: 'pointer' },
    },
    divider: { height: 1, backgroundColor: colors.grayThree, margin: '16px 0' },
    firstSection: { marginBottom: 24 },
    lastDivider: { marginTop: 'auto!important' },
    section: { marginBottom: 24, marginTop: 24 },
    mainContainer: { marginLeft: '-150px', marginTop: '40px' },
});

const styles = StyleSheet.create({
    filterTitle: { fontSize: 12, fontWeight: '700', lineHeight: 18 },
    headerTitle: { fontSize: 16, fontWeight: '500', lineHeight: 24 },
});

interface Props {
    onClose: () => void;
    clearFilters: () => void;
}

const Divider: FC<{ className?: string }> = ({ className }) => <hr className={className} />;

export const CVTQuickFilters: FC<Props> = ({ onClose, clearFilters }) => {
    const queryParams = useQuery();
    const filterOptions = getFilterOptions();
    const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: string }[]>(
        queryParams.getAll('statuses').map((status) => ({ label: status, value: status }))
    );

    const [startDate, setStartDate] = useState<string | null>(queryParams.get('startDate') ?? null);
    const [endDate, setEndDate] = useState<string | null>(queryParams.get('endDate') ?? null);

    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const classes = useStyles();

    const rightPanelDiv = document.getElementById('right-side-panel');
    if (!rightPanelDiv) return null;

    const applyAndClose = () => {
        if (startDate && !endDate) {
            return;
        } else if (endDate && !startDate) {
            return;
        }

        const params = new URLSearchParams(search);

        if (startDate) params.set('startDate', startDate);
        else params.delete('startDate');

        if (endDate) params.set('endDate', endDate);
        else params.delete('endDate');

        if (selectedStatus.length > 0) {
            selectedStatus.forEach((status) => {
                params.append('statuses', status.value);
            });
        } else {
            params.delete('statuses');
        }

        navigate(`${pathname}?${params.toString()}`);
        onClose();
    };

    const isApplyDisabled = selectedStatus.length === 0 && !Boolean(startDate) && !Boolean(endDate);
    const disableApply = () => {
        if (startDate && !endDate) {
            return true;
        } else if (endDate && !startDate) {
            return true;
        }
        return false;
    };

    return ReactDOM.createPortal(
        <div className={classes.panel}>
            <div className={classes.panelHeader}>
                <Typography style={styles.headerTitle}>Filters</Typography>
                <div className={classes.closeButton} onClick={onClose}>
                    <Icon name="x-close" />
                </div>
            </div>
            <div className={classes.panelScrollContainer}>
                <div className={classes.firstSection}>
                    <div style={{ marginBottom: 12, display: 'block' }}>
                        <Typography style={styles.filterTitle}>Date Range</Typography>
                    </div>
                    <div className={classes.dateContainer}>
                        <InputPickerHelper
                            type="date"
                            onChange={setStartDate}
                            iconName={'calendar'}
                            data={startDate}
                            mainContainerClassName={classes.mainContainer}
                            readonly={false}
                            datePlaceholder="Select start date"
                            errorMessage={!startDate && endDate ? 'Please select a start date' : ''}
                        />
                    </div>
                    <div className={classes.dateContainer}>
                        <InputPickerHelper
                            type="date"
                            onChange={setEndDate}
                            iconName={'calendar'}
                            data={endDate}
                            mainContainerClassName={classes.mainContainer}
                            readonly={false}
                            datePlaceholder="Select end date"
                            errorMessage={startDate && !endDate ? 'Please select an end date' : ''}
                        />
                    </div>
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Typography style={styles.filterTitle}>Status</Typography>
                    <Typeahead
                        multiple
                        className="mt-3"
                        onChange={(selected) => setSelectedStatus(selected as any)}
                        options={filterOptions.status}
                        placeholder="Select status"
                        selected={selectedStatus}
                        labelKey="label"
                    />
                </div>

                <Divider className={`${classes.divider} ${classes.lastDivider}`} />
                <div className={classes.actionButtons}>
                    <Button onPress={applyAndClose} disabled={isApplyDisabled || disableApply()}>
                        Apply
                    </Button>
                    <Button
                        variant="ghost-blue"
                        disabled={isApplyDisabled}
                        onPress={() => {
                            clearFilters();
                            onClose();
                        }}
                    >
                        Clear all
                    </Button>
                </div>
            </div>
        </div>,
        rightPanelDiv
    );
};
